import { createContext, useContext, useEffect, useState } from "react";
import { AVISO_HREF, CORREO_CONTACTO } from "@/lib/aviso";

const TRANSLATIONS = {
  en: {
    nav: {
      links: [
        { id: "plans", label: "Plans" },
        { id: "coverage", label: "Coverage" },
      ],
      cta: "Start an Engagement",
    },
    hero: {
      overline: "Atlantis International — Supply Chain Governance",
      lines: ["Solar procurement,", "governed", "end to end."],
      serifLine: 1,
      tagline:
        "Secure procurement, technical audits, and E2E supply chain governance for solar infrastructure investments in Mexico.",
      cta: "View Engagement Models",
      scroll: "Explore the four mandates",
      stats: [
        ["From $5,000 USD", "Per-project engagements"],
        ["$2,500 USD / mo", "Strategic annual retainer"],
        ["$60–90K USD", "Enterprise governance / year"],
      ],
    },
    pricing: {
      legal: "Payments are processed securely by Stripe. By contracting, you agree that Atlantis International uses your data to handle your request, as described in its privacy notice.",
      overline: "Engagement Models",
      titleA: "Integrated ",
      titleEm: "Service",
      titleB: " Packages",
      sub: "Sourcing, Audit, and Legal Compliance solutions for Energy Infrastructure. Every engagement is executed in USD with secure Stripe checkout.",
      plans: {
        essential: {
          kicker: "Per Project",
          price: "From $5,000",
          unit: "USD / project",
          subtitle: "For pilot projects / initial transactions.",
          desc: "Ideal for: Small companies and new importers.",
          features: [
            "Qualified Strategic Sourcing",
            "1 In-Situ Logistics Audit",
            "1 Pre-Shipment Inspection",
            "NCNDA Legal Framework Included",
          ],
        },
        strategic: {
          kicker: "Annual Retainer",
          badge: "Most Popular",
          price: "$2,500",
          unit: "USD / mo",
          subtitle: "Annual contract ($30,000 USD / year).",
          desc: "Ideal for: Mid-size companies with recurring operations.",
          features: [
            "Continuous E2E Management",
            "Up to 2 Sourcing Services",
            "2 Logistics Audits per year",
            "2 Pre-Shipment Inspections",
          ],
        },
        enterprise: {
          kicker: "Governance Mandate",
          price: "Custom Quote",
          unit: null,
          subtitle: "$60,000 - $90,000 USD / year.",
          desc: "Ideal for: Massive photovoltaic developers and consortiums.",
          features: [
            "Dedicated continuous sourcing",
            "4 On-Site Factory Audits",
            "6 Technical Inspections",
            "1 Tailored Nearshoring Study",
          ],
        },
      },
    },
    custom: {
      subtitle: "Dynamic quote builder for individual services.",
      select: "Select your requirements:",
      empty: "Select at least one service to proceed",
      state: (n, total) => `${n} service${n > 1 ? "s" : ""} selected · $${total.toLocaleString("en-US")} USD`,
      toasts: {
        empty: "Please select at least one service to proceed.",
        error: "Could not start checkout. Please try again.",
        soonTitle: "Online payments coming soon",
        soonDesc: "Contact us to secure your plan. We'll reply within 24 business hours.",
        soonContact: "Contact us",
      },
    },
    matrix: {
      overline: "Audit Coverage Matrix",
      titleA: "What each mandate ",
      titleEm: "covers.",
      capability: "Capability",
      descs: {
        sourcing:
          "From $2,500 / proj. · Market mapping, pre-selection, document validation and shortlist. + 3% to 7% commission on purchase value if procurement is managed.",
        factory_audit:
          "$1,250 - $1,500 / aud. · On-site assessment of capacity, quality and photo scorecard. Fixed travel expenses excluded.",
        e2e_supply_chain:
          "From $2,500 / mo · Full E2E management from supplier to final destination. Logistics and incident coordination.",
        nearshoring_study:
          "$5,000 - $7,500 / proj. · Technical/financial comparative study vs. Asia, T-MEC risks and roadmap. Complex projects from $10,000.",
        pre_shipment:
          "$700 / insp. · Pre-shipment visual inspection, sampling and evidence. External inspectors: $200/day + travel.",
        logistics_audit:
          "$1,000 / aud. · Route analysis, lead times, critical costs and freight optimization.",
      },
      rows: [
        {
          id: "sourcing",
          cap: "Strategic Sourcing & Matchmaking",
          values: [{ text: "Included", hl: true }, "Up to 2 per year", "Continuous", "+$2,500 per proj."],
        },
        {
          id: "factory_audit",
          cap: "On-Site Factory Audits",
          values: ["—", "2 per year", "4 per year", "+$1,250 per aud."],
        },
        {
          id: "e2e_supply_chain",
          cap: "E2E Supply Chain",
          values: ["—", { text: "Included", hl: true }, { text: "Full E2E Management", hl: true }, "+$2,500 / mo"],
        },
        {
          id: "nearshoring_study",
          cap: "Nearshoring Study",
          values: ["—", "—", "1 full study", "+$5,000 per proj."],
        },
        {
          id: "pre_shipment",
          cap: "Pre-Shipment Inspections",
          values: ["1", "2", "6", "+$700 per insp."],
        },
        {
          id: "logistics_audit",
          cap: "Logistics Audits",
          values: ["1", "—", "—", "+$1,000 per aud."],
        },
      ],
    },
    modal: {
      consent: {
        privacyA: "I have read the ",
        privacyLink: "privacy notice",
        privacyB: " and agree that Atlantis International uses my data to handle my request.",
        marketing:
          "I agree to receive commercial follow-up and news from Atlantis International and its group, and that my request is prioritized automatically. I can unsubscribe at any time.",
      },
      overline: "Enterprise Engagement",
      titleA: "Request a ",
      titleEm: "bespoke",
      titleB: " mandate.",
      desc: "For industrial companies and utility-scale solar project developers evaluating governance retainers between $60,000 and $90,000 USD / year.",
      fields: {
        name: "Full Name",
        email: "Work Email",
        company: "Company",
        capacity_mw: "Project Capacity (MW)",
        message: "Scope / Message",
      },
      submit: "Submit Engagement Request",
      successTitle: "Request received.",
      successBody:
        "Your enterprise engagement request has been logged. Our team will reach out to design a bespoke mandate.",
      done: "Done",
      toasts: {
        invalid: "Please complete full name, work email and company.",
        consent: "Please accept the privacy notice to send your request.",
        success: "Enterprise request received.",
        error: "Could not submit your request. Please try again.",
      },
    },
    payment: {
      cancelledTitleA: "Engagement ",
      cancelledEm: "cancelled.",
      paidTitleA: "Payment ",
      paidEm: "confirmed.",
      pendingTitleA: "Finalizing your ",
      pendingEm: "session…",
      cancelledBody:
        "No charge was made. You can come back and select your engagement model whenever you are ready.",
      paidBody:
        "Your engagement is confirmed. A member of the Atlantis team will contact you to initiate the mandate.",
      pendingBody: "We are confirming your payment with Stripe. This usually takes a few seconds.",
      pdf: "Download Quote (PDF)",
      back: "Back to Plans",
    },
    footer: {
      division: "Photovoltaic Client Division",
      links: [
        { label: "Portal Terms", href: "#" },
        { label: "Privacy Notice", href: AVISO_HREF },
        { label: "Client Support", href: `mailto:${CORREO_CONTACTO}` },
      ],
      rights: "All rights reserved.",
    },
  },
  es: {
    nav: {
      links: [
        { id: "plans", label: "Planes" },
        { id: "coverage", label: "Cobertura" },
      ],
      cta: "Iniciar Contratación",
    },
    hero: {
      overline: "Atlantis International — Gobernanza de Cadena de Suministro",
      lines: ["Procurement solar,", "gobernado", "de punta a punta."],
      serifLine: 1,
      tagline:
        "Adquisiciones seguras, auditorías técnicas y gobernanza E2E de la cadena de suministro para inversiones en infraestructura solar en México.",
      cta: "Ver Modelos de Contratación",
      scroll: "Explora los cuatro mandatos",
      stats: [
        ["Desde $5,000 USD", "Contrataciones por proyecto"],
        ["$2,500 USD / mes", "Retainer Strategic anual"],
        ["$60–90K USD", "Gobernanza Enterprise al año"],
      ],
    },
    pricing: {
      legal: "Los pagos se procesan de forma segura en Stripe. Al contratar, aceptas que Atlantis International use tus datos para atender tu solicitud, conforme a su aviso de privacidad.",
      overline: "Modelos de Contratación",
      titleA: "Paquetes Integrales ",
      titleEm: "Atlantis",
      titleB: "",
      sub: "Soluciones de Sourcing, Auditoría y Cumplimiento Legal para Infraestructura Energética. Cada contratación se ejecuta en USD con checkout seguro vía Stripe.",
      plans: {
        essential: {
          kicker: "Por Proyecto",
          price: "Desde $5,000",
          unit: "USD",
          subtitle: "Por proyecto piloto / transacciones iniciales.",
          desc: "Ideal para: Empresas pequeñas y nuevos importadores.",
          features: [
            "Strategic Sourcing calificado",
            "1 Auditoría Logística In-Situ",
            "1 Inspección Pre-Embarque",
            "Marco Legal NCNDA Incluido",
          ],
        },
        strategic: {
          kicker: "Retainer Anual",
          badge: "Más Popular",
          price: "$2,500",
          unit: "USD / mes",
          subtitle: "Contrato anual ($30,000 USD / año).",
          desc: "Ideal para: Empresas medianas con operaciones recurrentes.",
          features: [
            "Gestión E2E continua",
            "Hasta 2 Servicios de Sourcing",
            "2 Auditorías Logísticas al año",
            "2 Inspecciones Pre-Embarque",
          ],
        },
        enterprise: {
          kicker: "Mandato de Gobernanza",
          price: "Custom Quote",
          unit: null,
          subtitle: "$60,000 - $90,000 USD / año.",
          desc: "Ideal para: Desarrolladores fotovoltaicos masivos y consorcios.",
          features: [
            "Sourcing continuo dedicado",
            "4 Auditorías a Fábricas (Factory Audits)",
            "6 Inspecciones técnicas",
            "1 Estudio de Nearshoring a la medida",
          ],
        },
      },
    },
    custom: {
      subtitle: "Cotizador dinámico de servicios individuales.",
      select: "Selecciona tus requerimientos:",
      empty: "Selecciona al menos un servicio para proceder",
      state: (n, total) =>
        `${n} servicio${n > 1 ? "s" : ""} seleccionado${n > 1 ? "s" : ""} · $${total.toLocaleString("en-US")} USD`,
      toasts: {
        empty: "Por favor selecciona al menos un servicio para proceder.",
        error: "No se pudo iniciar el checkout. Inténtalo de nuevo.",
        soonTitle: "Pagos en línea muy pronto",
        soonDesc: "Contáctanos para asegurar tu plan. Te respondemos en un máximo de 24 horas hábiles.",
        soonContact: "Contáctanos",
      },
    },
    matrix: {
      overline: "Matriz de Cobertura de Auditorías",
      titleA: "Qué cubre cada ",
      titleEm: "mandato.",
      capability: "Capabilidad",
      descs: {
        sourcing:
          "Desde $2,500 / proy. · Market mapping, preselección, validación documental y shortlist. + Comisión de 3% a 7% sobre valor de compra si se gestiona el procurement.",
        factory_audit:
          "$1,250 - $1,500 / aud. · Evaluación presencial de capacidad, calidad y scorecard fotográfico. Excluye viáticos fijos.",
        e2e_supply_chain:
          "Desde $2,500 / mes · Gestión integral E2E desde proveedor hasta destino final. Coordinación logística y de incidencias.",
        nearshoring_study:
          "$5,000 - $7,500 / proy. · Estudio comparativo técnico/financiero vs. Asia, riesgos T-MEC y roadmap. Proyectos complejos desde $10,000.",
        pre_shipment:
          "$700 / insp. · Inspección visual previa al embarque, muestreo y evidencia. Inspectores externos: $200/día + viáticos.",
        logistics_audit:
          "$1,000 / aud. · Análisis de rutas, lead times, costos críticos y optimización de fletes.",
      },
      rows: [
        {
          id: "sourcing",
          cap: "Strategic Sourcing & Matchmaking",
          values: [{ text: "Incluido", hl: true }, "Hasta 2 al año", "Continuo", "+$2,500 por proy."],
        },
        {
          id: "factory_audit",
          cap: "Auditorías a Fábricas In-Situ",
          values: ["—", "2 al año", "4 al año", "+$1,250 por aud."],
        },
        {
          id: "e2e_supply_chain",
          cap: "E2E Supply Chain",
          values: ["—", { text: "Incluido", hl: true }, { text: "Gestión E2E completa", hl: true }, "+$2,500 / mes"],
        },
        {
          id: "nearshoring_study",
          cap: "Estudio de Nearshoring",
          values: ["—", "—", "1 estudio completo", "+$5,000 por proy."],
        },
        {
          id: "pre_shipment",
          cap: "Inspecciones Pre-Embarque",
          values: ["1", "2", "6", "+$700 por insp."],
        },
        {
          id: "logistics_audit",
          cap: "Auditorías Logísticas In-Situ",
          values: ["1", "—", "—", "+$1,000 por aud."],
        },
      ],
    },
    modal: {
      consent: {
        privacyA: "He leído el ",
        privacyLink: "aviso de privacidad",
        privacyB: " y acepto que Atlantis International use mis datos para atender mi solicitud.",
        marketing:
          "Acepto recibir seguimiento comercial y novedades de Atlantis International y de su grupo, y que mi solicitud se priorice de forma automatizada. Puedo darme de baja cuando quiera.",
      },
      overline: "Contratación Enterprise",
      titleA: "Solicita un mandato ",
      titleEm: "a la medida.",
      titleB: "",
      desc: "Para empresas industriales y desarrolladores de proyectos solares a escala utility que evalúan retainers de gobernanza entre $60,000 y $90,000 USD / año.",
      fields: {
        name: "Nombre Completo",
        email: "Correo de Trabajo",
        company: "Empresa",
        capacity_mw: "Capacidad del Proyecto (MW)",
        message: "Alcance / Mensaje",
      },
      submit: "Enviar Solicitud de Contratación",
      successTitle: "Solicitud recibida.",
      successBody:
        "Tu solicitud de contratación Enterprise ha sido registrada. Nuestro equipo te contactará para diseñar un mandato a la medida.",
      done: "Listo",
      toasts: {
        invalid: "Por favor completa nombre, correo de trabajo y empresa.",
        consent: "Para enviar tu solicitud acepta el aviso de privacidad.",
        success: "Solicitud Enterprise recibida.",
        error: "No se pudo enviar tu solicitud. Inténtalo de nuevo.",
      },
    },
    payment: {
      cancelledTitleA: "Contratación ",
      cancelledEm: "cancelada.",
      paidTitleA: "Pago ",
      paidEm: "confirmado.",
      pendingTitleA: "Finalizando tu ",
      pendingEm: "sesión…",
      cancelledBody:
        "No se realizó ningún cargo. Puedes volver y seleccionar tu modelo de contratación cuando estés listo.",
      paidBody:
        "Tu contratación está confirmada. Un miembro del equipo Atlantis te contactará para iniciar el mandato.",
      pendingBody: "Estamos confirmando tu pago con Stripe. Esto suele tardar unos segundos.",
      pdf: "Descargar Cotización (PDF)",
      back: "Volver a los Planes",
    },
    footer: {
      division: "División de Clientes Fotovoltaicos",
      links: [
        { label: "Términos del Portal", href: "#" },
        { label: "Aviso de Privacidad", href: AVISO_HREF },
        { label: "Soporte a Clientes", href: `mailto:${CORREO_CONTACTO}` },
      ],
      rights: "Todos los derechos reservados.",
    },
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: TRANSLATIONS.en });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = window.localStorage.getItem("atlantis-lang");
      return saved === "es" ? "es" : "en";
    } catch (e) {
      return "en";
    }
  });

  const setLang = (l) => {
    setLangState(l);
    try {
      window.localStorage.setItem("atlantis-lang", l);
    } catch (e) {
      /* private mode */
    }
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t: TRANSLATIONS[lang] }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);